import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Mono column for changing values only (codes, times, numbers). */
  mono?: boolean;
  render: (row: T) => ReactNode;
  /** A column the server sorts by: the header says which way (the header holds the button). */
  ariaSort?: 'ascending' | 'descending' | 'none';
}

interface TableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  onRowClick?: ((row: T) => void) | undefined;
  selectedKey?: string | null | undefined;
  compact?: boolean;
  caption: string;
}

/** Data table with an accessible caption; charts elsewhere always have such a table nearby (rule 22). */
export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  compact = false,
  caption,
}: TableProps<T>) {
  return (
    <div className={['tbl-wrap', compact ? 'compact' : ''].join(' ').trim()}>
      <table className="tbl">
        <caption
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={c.mono ? 'm' : undefined}
                scope="col"
                aria-sort={c.ariaSort}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = rowKey(row);
            const classes = [
              onRowClick === undefined ? '' : 'clickable',
              key === selectedKey ? 'sel' : '',
            ]
              .join(' ')
              .trim();
            return (
              <tr
                key={key}
                className={classes === '' ? undefined : classes}
                onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                tabIndex={onRowClick === undefined ? undefined : 0}
                onKeyDown={
                  onRowClick === undefined
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                }
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.mono ? 'm' : undefined}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
